import { Injectable } from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';

export interface ImportProgress {
  isActive: boolean;
  operation: string;
  userName: string;
  total: number;
  completed: number;
  percentage: number;
  // 'reading' and 'analyzing' cover the pre-row work, which has no countable
  // unit but is most of the wall clock on a large file.
  phase?: 'reading' | 'analyzing' | 'processing' | 'saving' | 'confirming' | 'confirm-saving' | 'pushing' | 'push-saving' | 'generating-feed';
}

export interface SyncProgress {
  totalItems: number;
  completedItems: number;
  percentage: number;
  startTime: Date | null;
  estimatedTimeRemaining: string;
  syncStatus: string;
  itemsPerSecond: number;
}

@Injectable({
  providedIn: 'root'
})
export class ProgressBarsService {
  public shopifySyncValue: Observable<any> | undefined;
  private shopifySyncValueSubject: BehaviorSubject<any> | undefined;

  public shopifySyncCounter: Observable<any> | undefined;
  private shopifySyncCounterSubject: BehaviorSubject<any> | undefined;

  public shopifySyncPendingStatus: Observable<any> | undefined;
  private shopifySyncPendingStatusSubject: BehaviorSubject<any> | undefined;

  public bulkValue: Observable<any> | undefined;
  private bulkValueSubject: BehaviorSubject<any> | undefined;

  private importUpdateBarSubject: BehaviorSubject<any> = new BehaviorSubject<any>(0);
  public importUpdateBar: Observable<any> = this.importUpdateBarSubject.asObservable();

  private importCreateBarSubject: BehaviorSubject<any> = new BehaviorSubject<any>(0);
  public importCreateBar: Observable<any> = this.importCreateBarSubject.asObservable();

  // Pre-import file analysis (server-side parse + catalog matching). Payload:
  // {phase: 'parsing', rows} | {phase: 'counting', completed, total, percent} | null
  private importAnalysisSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  public importAnalysis: Observable<any> = this.importAnalysisSubject.asObservable();

  // Enhanced sync tracking
  private syncProgressSubject: BehaviorSubject<SyncProgress> = new BehaviorSubject<SyncProgress>({
    totalItems: 0,
    completedItems: 0,
    percentage: 0,
    startTime: null,
    estimatedTimeRemaining: '',
    syncStatus: '',
    itemsPerSecond: 0
  });
  public syncProgress$: Observable<SyncProgress> = this.syncProgressSubject.asObservable();

  // Import progress tracking
  private importProgressSubject: BehaviorSubject<ImportProgress> = new BehaviorSubject<ImportProgress>({
    isActive: false,
    operation: '',
    userName: '',
    total: 0,
    completed: 0,
    percentage: 0,
  });
  public importProgress$: Observable<ImportProgress> = this.importProgressSubject.asObservable();
  private importEndTimer: any = null;

  // Sliding window of recent samples for throughput calculation.
  // Instead of averaging speed over the entire sync (which produces
  // wildly inaccurate ETAs after a page reload mid-sync), we only
  // look at the last ~200 items worth of progress ticks.
  private recentSamples: { time: number; completed: number }[] = [];
  private readonly SAMPLE_WINDOW_ITEMS = 200;

  // Track reset timer to prevent conflicts
  private resetTimer: any = null;

  // Guard: once sync is complete, ignore stale counter events until next startSync()
  private isSyncComplete: boolean = false;

  constructor() {
    this.shopifySyncValueSubject = new BehaviorSubject<any>(0);
    this.shopifySyncValue = this.shopifySyncValueSubject.asObservable();

    this.shopifySyncCounterSubject = new BehaviorSubject<any>(0);
    this.shopifySyncCounter = this.shopifySyncCounterSubject.asObservable();

    this.shopifySyncPendingStatusSubject = new BehaviorSubject<any>(false);
    this.shopifySyncPendingStatus = this.shopifySyncPendingStatusSubject.asObservable();

    this.bulkValueSubject = new BehaviorSubject<any>(0);
    this.bulkValue = this.bulkValueSubject.asObservable();

    // this.importUpdateBarSubject = new BehaviorSubject<any>(0);
    // this.importUpdateBar = this.importUpdateBarSubject.asObservable();
  }

  public updateImportAnalysis(payload: any): void {
    if (payload && payload.phase === 'counting' && payload.total > 0) {
      payload = { ...payload, percent: Math.round((payload.completed / payload.total) * 100) };
    }
    this.importAnalysisSubject?.next(payload);
  }

  public setShopifyValue(value: number): void {
    this.shopifySyncValueSubject?.next(value);
  }

  public setShopifyCounter(value: number): void {
    this.shopifySyncCounterSubject?.next(value);
  }

  public setShopifyPendingStatus(value: boolean): void {
    this.shopifySyncPendingStatusSubject?.next(value);
  }

  public setBulkValue(value: number): void {
    this.bulkValueSubject?.next(value);
  }

  public setImportUpdateValue(value: number): void {
    this.importUpdateBarSubject?.next(value);
  }

  public setImportCreateValue(value: number): void {
    this.importCreateBarSubject?.next(value);
  }

  // Import progress methods
  public startImport(operation: string, userName: string, total: number): void {
    if (this.importEndTimer) {
      clearTimeout(this.importEndTimer);
      this.importEndTimer = null;
    }
    this.importProgressSubject.next({
      isActive: true,
      operation,
      userName,
      total,
      completed: 0,
      percentage: 0,
      phase: 'processing',
    });
  }

  // Phase-only update: the work has no countable unit yet (parsing, tag
  // resolution), so name what is happening and leave the numbers alone.
  public setImportPhase(phase: ImportProgress['phase']): void {
    const current = this.importProgressSubject.value;
    this.importProgressSubject.next({...current, isActive: true, phase});
  }

  public updateImportProgress(completed: number, total: number, percentage: number): void {
    const current = this.importProgressSubject.value;
    this.importProgressSubject.next({
      ...current,
      completed,
      total,
      percentage,
    });
  }

  public startSavingPhase(completed: number, total: number, percentage: number): void {
    if (this.importEndTimer) {
      clearTimeout(this.importEndTimer);
      this.importEndTimer = null;
    }
    const current = this.importProgressSubject.value;
    this.importProgressSubject.next({
      ...current,
      phase: 'saving',
      completed,
      total,
      percentage,
    });
  }

  // Live status of a running Shopify bulk operation (per store), rendered as a
  // detail line under the sync bar.
  private bulkSyncStatusSubject: BehaviorSubject<any> = new BehaviorSubject<any>({});
  public bulkSyncStatus$: Observable<any> = this.bulkSyncStatusSubject.asObservable();

  public setBulkSyncStatus(payload: any): void {
    const current = { ...this.bulkSyncStatusSubject.value };
    if (payload?.store) {
      if (payload.state === 'done' || payload.state === 'failed') {
        // Run-level end: drop the store's line and every per-operation line.
        for (const key of Object.keys(current)) {
          if (key === payload.store || key.startsWith(payload.store + '|')) {
            delete current[key];
          }
        }
      } else if (payload.opKey) {
        // Concurrent bulk operations each get their own line, keyed per
        // operation; the store-level "preparing" line ends when the first
        // operation starts reporting.
        delete current[payload.store];
        if (payload.state === 'op-done') {
          delete current[payload.store + '|' + payload.opKey];
        } else {
          current[payload.store + '|' + payload.opKey] = payload;
        }
      } else {
        current[payload.store] = payload;
      }
      this.bulkSyncStatusSubject.next(current);
    }
  }

  public clearBulkSyncStatus(): void {
    this.bulkSyncStatusSubject.next({});
  }

  // Drives the same banner for the Update & Sync confirm/push phases.
  public updatePushProgress(phase: 'confirming' | 'confirm-saving' | 'pushing' | 'push-saving' | 'generating-feed', completed: number, total: number, percentage: number): void {
    if (this.importEndTimer) {
      clearTimeout(this.importEndTimer);
      this.importEndTimer = null;
    }
    this.importProgressSubject.next({
      isActive: true,
      operation: 'push',
      userName: '',
      phase,
      completed,
      total,
      percentage,
    });
    // Feed generation is the last push phase; once it reports complete the
    // sync bar owns the rest of the run — dismiss this banner instead of
    // letting it sit at 100% until the whole sync ends.
    if (phase === 'generating-feed' && total > 0 && completed >= total) {
      this.endImport();
    }
  }

  public endImport(): void {
    const current = this.importProgressSubject.value;
    this.importProgressSubject.next({
      ...current,
      completed: current.total,
      percentage: 100,
    });
    // Show 100% for 3 seconds then hide
    this.importEndTimer = setTimeout(() => {
      this.importProgressSubject.next({
        isActive: false,
        operation: '',
        userName: '',
        total: 0,
        completed: 0,
        percentage: 0,
      });
      this.importEndTimer = null;
    }, 3000);
  }

  // Enhanced sync tracking methods
  public startSync(totalItems: number): void {
    // Clear any pending reset timer when starting new sync
    if (this.resetTimer) {
      clearTimeout(this.resetTimer);
      this.resetTimer = null;
    }

    this.isSyncComplete = false;

    const progress: SyncProgress = {
      totalItems,
      completedItems: 0,
      percentage: 0,
      startTime: new Date(),
      estimatedTimeRemaining: 'Calculating...',
      syncStatus: 'Starting sync...',
      itemsPerSecond: 0
    };
    this.syncProgressSubject.next(progress);
    this.recentSamples = [];

    // Also update legacy observables for backward compatibility
    this.setShopifyCounter(totalItems);
    this.setShopifyValue(0);
    this.setShopifyPendingStatus(false);
  }

  public updateSyncProgress(completedItems: number, totalItems: number): void {
    // Ignore stale counter events that arrive after sync completion
    if (this.isSyncComplete) {
      return;
    }

    const current = this.syncProgressSubject.value;

    // ── Sliding-window throughput ──
    // Push every progress tick into a circular buffer.
    // Trim so we only keep the last SAMPLE_WINDOW_ITEMS worth of
    // data points. Throughput = deltaItems / deltaTime across the
    // window, giving us an accurate *recent* rate no matter when
    // the page was loaded.
    const now = Date.now();
    this.recentSamples.push({ time: now, completed: completedItems });

    // Trim: remove entries older than SAMPLE_WINDOW_ITEMS items ago,
    // but always keep at least 2 so we can calculate a delta.
    const minCompleted = completedItems - this.SAMPLE_WINDOW_ITEMS;
    while (this.recentSamples.length > 2 && this.recentSamples[0].completed < minCompleted) {
      this.recentSamples.shift();
    }

    let avgSpeed = 0;
    if (this.recentSamples.length >= 2) {
      const oldest = this.recentSamples[0];
      const newest = this.recentSamples[this.recentSamples.length - 1];
      const itemsDelta = newest.completed - oldest.completed;
      const timeDelta = (newest.time - oldest.time) / 1000; // seconds
      // Need at least 3 seconds and some items to have meaningful data
      if (timeDelta >= 3 && itemsDelta > 0) {
        avgSpeed = itemsDelta / timeDelta;
      }
    }

    // Calculate remaining items and time
    const remainingItems = totalItems - completedItems;
    const estimatedSecondsRemaining = avgSpeed > 0 ? remainingItems / avgSpeed : 0;

    // Format estimated time
    const estimatedTimeRemaining = this.formatTime(estimatedSecondsRemaining);

    // Calculate percentage
    const percentage = totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;

    // Determine status message
    let syncStatus = 'Syncing...';
    if (percentage === 100) {
      syncStatus = 'Complete!';
    } else if (percentage >= 95) {
      syncStatus = 'Finalizing...';
    } else if (percentage === 0) {
      syncStatus = 'Preparing...';
    } else if (percentage < 25) {
      syncStatus = 'Starting...';
    }

    const progress: SyncProgress = {
      totalItems,
      completedItems,
      percentage,
      startTime: current.startTime, // Preserve startTime if we saw the real start via startSync()
      estimatedTimeRemaining,
      syncStatus,
      itemsPerSecond: Math.round(avgSpeed * 10) / 10 // Round to 1 decimal
    };

    this.syncProgressSubject.next(progress);

    // Update legacy observables
    this.setShopifyValue(percentage);
    this.setShopifyCounter(remainingItems);
  }

  public completeSync(): void {
    this.isSyncComplete = true;

    // Clear any existing reset timer
    if (this.resetTimer) {
      clearTimeout(this.resetTimer);
      this.resetTimer = null;
    }

    const current = this.syncProgressSubject.value;
    const progress: SyncProgress = {
      ...current,
      completedItems: current.totalItems,
      percentage: 100,
      estimatedTimeRemaining: '',
      syncStatus: 'Complete!',
      itemsPerSecond: 0
    };

    this.syncProgressSubject.next(progress);

    // Reset after a delay, but store the timer reference
    this.resetTimer = setTimeout(() => {
      this.resetSync();
      this.resetTimer = null;
    }, 3000); // Reduced from 5000ms to 3000ms for better UX
  }

  public resetSync(): void {
    this.isSyncComplete = false;

    const progress: SyncProgress = {
      totalItems: 0,
      completedItems: 0,
      percentage: 0,
      startTime: null,
      estimatedTimeRemaining: '',
      syncStatus: '',
      itemsPerSecond: 0
    };

    this.syncProgressSubject.next(progress);
    this.recentSamples = [];

    // Reset legacy observables
    this.setShopifyValue(0);
    this.setShopifyCounter(0);
    this.setShopifyPendingStatus(false);
  }

  private formatTime(seconds: number): string {
    if (seconds <= 0 || !isFinite(seconds)) {
      return '';
    }

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  }
}
